/**
 * The two controls the Ankiety tab's two heads draw, and the sentence that says a write did not land.
 *
 * ── Why they are a feature's and not a page's ────────────────────────────────────────────────
 * Promoted with the report on 17 September 2026: the report DIALOG draws the mark and the failure
 * sentence in its own head, and a feature may not reach into a page. What keeps them one component
 * each rather than three buttons is that neither is a button with a glyph in it — the mark carries `aria-busy` and
 * swaps its tick for a hollow ring while the write is in flight, the refusable one keeps
 * `aria-disabled` rather than the attribute so the reason attached to it stays reachable, and both
 * guard the press at the component rather than at the call site. Those are the halves the next copy
 * would lose, and the previous copies had already parted — one head computed its name twice, once
 * for `aria-label` and once for `title`.
 *
 * The GEOMETRY stays at the call site, as `shared/ui/iconButton.module.css` requires: the stream's
 * head button stands on a chip ground and the pane's on a transparent one, so each passes its own
 * class and nothing is declared on both sides.
 */

import type { ReactNode, Ref } from 'react';

import ink from '~/shared/ui/inkButton.module.css';
import icon from '~/shared/ui/iconButton.module.css';

import { ReviewedIcon, WaitingIcon } from './SurveyIcons';
import styles from './SurveyActions.module.css';

export interface RefusableIconButtonProps {
  /** The control's name when it can be pressed. */
  readonly label: string;
  /**
   * Why it cannot be pressed, which REPLACES the name rather than being appended to it.
   *
   * `null` when it can. A refused control keeps `aria-disabled` and stays in the tab order — a
   * reason nothing can reach is not a reason — and the press is guarded here rather than at each
   * call site.
   */
  readonly refusedReason: string | null;
  readonly onPress: () => void;
  /** The surface's own geometry class. See the header. */
  readonly className: string;
  readonly children: ReactNode;
  /** For a surface that hands focus back to this control when the one that had it disappears. */
  readonly ref?: Ref<HTMLButtonElement>;
}

export function RefusableIconButton({
  label,
  refusedReason,
  onPress,
  className,
  children,
  ref,
}: RefusableIconButtonProps) {
  const name = refusedReason ?? label;
  return (
    <button
      ref={ref}
      type="button"
      className={`${String(icon.iconButton)} ${className}`}
      aria-disabled={refusedReason !== null}
      aria-label={name}
      title={name}
      onClick={() => {
        if (refusedReason === null) onPress();
      }}
    >
      {children}
    </button>
  );
}

export interface MarkReviewedButtonProps {
  /** „Oznacz tydz. 8 jako przejrzaną" — the occurrence is named, not an unlabelled tick. */
  readonly label: string;
  /** The write is in flight. Said through `aria-busy` and the ring, never through a dim. */
  readonly marking: boolean;
  readonly onMark: () => void;
  /**
   * The surface's own GEOMETRY class — the six properties `shared/ui/inkButton.module.css`
   * requires of a surface, plus the square's `width`. The ink treatment is this component's.
   */
  readonly className: string;
}

/**
 * Write-once: `POST /survey/assignment/{id}/mark-reviewed` has no inverse, no „odznacz" exists on
 * the contract and neither surface draws one. The caller removes this control once `reviewedAt` is
 * set rather than disabling it.
 *
 * ── It wears the INK PRIMARY, the same treatment the client detail's other primaries wear ─────
 * It wore the ember primary until 17 September 2026, when the owner saw the report dialog and
 * asked for the square to be white with no glow: at 28px the ember's lift reads as a coloured halo
 * rather than as a lip, and the dialog already spends its one accent on the row's due-date dot.
 * `shared/ui/inkButton.module.css` is flat and un-accented by design, so the control composes that
 * file with the surface's square geometry and adds no shadow of its own. Earlier it had been an
 * `.ember` modifier bolted onto the icon button, whose hairline fought the treatment's lips
 * (owner, 16 September 2026: "kreski") — the lesson stands: a primary wears exactly one shared
 * treatment and never a local override of it.
 *
 * While the write is in flight the control is `aria-disabled` and therefore takes that treatment's
 * own refused fill — a neutral chip — rather than a dim. The ring says what is happening; the flat
 * neutral says the press will not land again.
 */
export function MarkReviewedButton({
  label,
  marking,
  onMark,
  className,
}: MarkReviewedButtonProps) {
  return (
    <button
      type="button"
      className={`${String(ink.ink)} ${className}`}
      aria-disabled={marking}
      aria-busy={marking}
      aria-label={label}
      title={label}
      onClick={() => {
        if (!marking) onMark();
      }}
    >
      {marking ? <WaitingIcon /> : <ReviewedIcon />}
    </button>
  );
}

/**
 * A write that did not land, beside the control that made it.
 *
 * It was at the BOTTOM of the matrix card, under a table the trainer had usually scrolled, and
 * behind the report's own backdrop whenever the mark was made from inside the dialog — so the one
 * thing that needed saying was the one thing nothing said. It stands in whichever head the control
 * stands in, which is why both dialogs and the matrix draw it.
 */
export function MarkFailedAlert({
  failed,
  message,
}: {
  readonly failed: boolean;
  readonly message: string;
}) {
  if (!failed) return null;
  return (
    <p className={styles.alert} role="alert">
      {message}
    </p>
  );
}
